import type {
  TrackUserSearchCareerAdminRequest,
  TrackUserViewCareerAdminRequest,
} from '~/api-types/red-analytics'

const urlRegex = new RegExp(/https?:\/\/[^\s]+/)

/**
 * ページID
 *
 * 左から桁数毎の意味
 * 1桁目: 主なデータ (1:求職者)
 * 2桁目: カテゴリ(0:一覧、1:詳細)
 * 3桁目: 1〜2が同じページでの連番
 */
export const PAGE_IDS: Record<TrackingPageCode, number> = {
  USERS_SCOUT_SEARCH: 101,
  USERS_TALENT_POOL: 102,
  USERS_INTENTION_UPDATE: 103,
  USERS_PROFILE_UPDATE: 104,
  USERS_INTERESTED: 105,
  // 代理モード限定ページのため実際にトラッキングが送信されることはない（型充足用）
  USERS_ASSIST_COMPANY_ALUMNI: 106,
  USER_DETAIL: 111,
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  if (!config.public.analyticsApiBaseUrl || !config.public.analyticsApiToken) {
    return
  }

  const analyticsApi = $fetch.create({
    baseURL: config.public.analyticsApiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Token': config.public.analyticsApiToken,
    },
  })

  const trackUserSearch = async ({
    pageCode,
    query,
  }: {
    pageCode: TrackingPageCode
    query: Omit<GetUserListRequest, 'take' | 'skip'>
  }) => {
    const { isAdmin, isAssistMode, accountInfo } = useAuth()
    // 代理モード中の運営の操作は企業のトラッキングデータに含めない
    if (isAdmin.value || isAssistMode.value || !accountInfo.value) {
      return
    }

    const body: TrackUserSearchCareerAdminRequest = {
      view_info: {
        page_id: PAGE_IDS[pageCode],
        company_code: accountInfo.value.companyCode,
        employee_code: accountInfo.value.employeeCode,
      },
      filters: {
        keyword: query.keyword,
        occupation_ids: query.occupationIds,
        skills: query.skills,
        career_tag_ids: query.careerTagIds,
        intention_ids: query.intentionIds,
        desired_condition_ids: query.desiredConditionIds,
        upper_age: query.upperAge,
        lower_age: query.lowerAge,
        portfolio_status: query.portfolioStatus
          ? Number(query.portfolioStatus)
          : undefined,
        desired_occupation_ids: query.desiredOccupationIds,
        desired_attendance_frequencies_ids:
          query.desiredAttendanceFrequenciesIds,
        desired_work_location_ids: query.desiredWorkLocationIds,
        show_all: query.showAll,
        only_has_draft: query.onlyHasDraft,
        current_income_max: query.currentIncomeMax,
        current_income_min: query.currentIncomeMin,
        desired_income_max: query.desiredIncomeMax,
        desired_income_min: query.desiredIncomeMin,
        prefecture_ids: query.prefectureIds,
        desired_income: query.desiredIncome,
        last_access_type: query.lastAccessType,
      },
    }

    try {
      await analyticsApi('/career/admin/search-users', {
        method: 'POST',
        body,
      })
    } catch (e) {
      console.error(e)
    }
  }

  const trackUserView = async ({
    pageCode,
    action,
    user,
  }: {
    pageCode: TrackingPageCode
    action: Exclude<TrackingUserViewAction, 'SCOUT'>
    user: UserSummary
  }) => {
    const { isAdmin, isAssistMode, accountInfo } = useAuth()
    // 代理モード中の運営の操作は企業のトラッキングデータに含めない
    if (isAdmin.value || isAssistMode.value || !accountInfo.value) {
      return
    }

    const career = getDisplayCareer(user)
    const careerName = getUserDisplayCareerName(career)
    const careerTags = getUserCareerTags(user)

    const body: TrackUserViewCareerAdminRequest = {
      view_info: {
        page_id: PAGE_IDS[pageCode],
        action: action,
        company_code: accountInfo.value.companyCode,
        employee_code: accountInfo.value.employeeCode,
      },

      user: {
        display_id: user.displayId,
        is_read: user.isRead,
        has_draft: user.hasDraft,
        scout_status: user.scout.scoutStatus,
        age: user.age,
        intention_work: user.intentionWork,
        intention_side_work: user.intentionSideWork,
        occupations: user.occupations.map((occupation) => ({
          id: occupation.id,
          experience_year: occupation.experienceYear,
        })),
        introduction_count: user.introduction.length,
        want_to_do_count: user.wantToDo?.length || 0,
        can_do_count: user.canDo?.length || 0,
        has_introduction_link: urlRegex.test(user.introduction),
        career_company_name: careerName,
        career_tags_count: careerTags.length,
        career_tags_ids: careerTags.map((tag) => tag.userTagId).join(','),
        has_public_portfolio: user.portfolios.some(
          (portfolio) =>
            portfolio.displayStatus === PortfolioDisplayStatus.PUBLIC,
        ),
        has_private_portfolio: user.portfolios.some(
          (portfolio) =>
            portfolio.displayStatus === PortfolioDisplayStatus.PRIVATE ||
            portfolio.displayStatus === PortfolioDisplayStatus.APPROVAL,
        ),
        comment_num: user.commentNum,
        has_input_display_skill_category: false,
        display_skill_category: undefined,
        current_income: Number(user.currentIncomeRange.split('-')[1]), // レンジの上限値を送信
        prefecture_id: user.prefectureId,
        last_accessed_at: user.lastAccessedAt,
      },
    }

    try {
      await analyticsApi('/career/admin/view-user', {
        method: 'POST',
        body,
      })
    } catch (e) {
      console.error(e)
    }
  }

  const trackUserScout = async ({
    pageCode,
    user,
    jobCodeToScout,
  }: {
    pageCode: TrackingPageCode
    user: GetUserDetail200ResponseUser
    jobCodeToScout: string
  }) => {
    const { isAdmin, isAssistMode, accountInfo } = useAuth()
    // 代理モード中の運営の操作は企業のトラッキングデータに含めない
    if (isAdmin.value || isAssistMode.value || !accountInfo.value) {
      return
    }

    const body: TrackUserViewCareerAdminRequest = {
      view_info: {
        page_id: PAGE_IDS[pageCode],
        action: 'SCOUT',
        company_code: accountInfo.value.companyCode,
        employee_code: accountInfo.value.employeeCode,
      },
      user: {
        display_id: user.displayId,
        is_read: user.isRead,
        has_draft: user.hasDraft,
        scout_status: user.scout.scoutStatus,
        age: user.age,
        intention_work: user.intentionWork,
        intention_side_work: user.intentionSideWork,
        occupations: user.occupations.map((occupation) => ({
          id: occupation.id,
          experience_year: occupation.experienceYear,
        })),
        introduction_count: user.introduction.length,
        want_to_do_count: user.wantToDo?.length || 0,
        can_do_count: user.canDo?.length || 0,
        has_introduction_link: urlRegex.test(user.introduction),
        career_company_name: getUserDisplayCareerName(getDisplayCareer(user)),
        career_tags_count: getUserCareerTags(user).length,
        career_tags_ids: getUserCareerTags(user)
          .map((tag) => tag.userTagId)
          .join(','),
        current_income: Number(user.currentIncomeRange.split('-')[1]), // レンジの上限値を送信
        prefecture_id: user.prefectureId,
        last_accessed_at: user.lastAccessedAt,
      },
      scout: {
        job_code: jobCodeToScout,
        is_referral_comment_displayed: Boolean(user.referralComment),
      },
    }

    try {
      await analyticsApi('/career/admin/view-user', {
        method: 'POST',
        body,
      })
    } catch (e) {
      console.error(e)
    }
  }

  return {
    provide: {
      trackUserSearch,
      trackUserView,
      trackUserScout,
    },
  }
})
